import React from "react";
import PropTypes from "prop-types";
import { isEmpty, isObject } from "lodash";

// Components
import LoadingSpinner from "../Atoms/LoadingSpinner";
import Seo from "../Utils/Seo";
import SleepHeroSection from "../Molecules/SleepHeroSection";
import FeaturedSleepProductSection from "../Molecules/FeaturedSleepProductSection";
import FeaturedProductsSection from "../Organisms/FeaturedProductsSection";
import { useSleepProductCollection } from "../Utils/SleepProductCollection";

// Stylesheets
import "./DreamShopCollectionPageBase.scss";

export const getBannerBackground = ({ bannerBackground }) => {
  if (bannerBackground) {
    return { style: { backgroundColor: bannerBackground } };
  }

  return null;
};

export const FeaturedProductsHeader = ({ subCollectionData: { metadata } }) => (
  <header className="header">
    <h2 className="primary_title caption_primary">
      <span className="label">Shop the collection:</span> {metadata.title}
    </h2>
  </header>
);

FeaturedProductsHeader.propTypes = {
  subCollectionData: PropTypes.object.isRequired,
};

export const IntroSection = ({ copy }) => (
  <section className="intro_section section">
    <div className="container">
      <h3 className="heading h3_text">{copy.header}</h3>
      {copy.body}
    </div>
  </section>
);

IntroSection.propTypes = {
  copy: PropTypes.object.isRequired,
};

export const FeaturedProductsIntroSection = ({ copy }) => {
  const intro = isObject(copy) ? copy.props.children : copy.toLowerCase();

  return (
    <section className="featured_products_intro_section section">
      <div className="container">
        <h3 className="heading h3_text">
          Here’s what else we’re loving {intro}...
        </h3>
      </div>
    </section>
  );
};

FeaturedProductsIntroSection.propTypes = {
  copy: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

/*
  Note: This page is only for sleep goal-specific collections.

  Rendering logic is handled in utils/routes.js.
*/
export const DreamShopCollectionPageBase = ({ staticData }) => {
  const { isFetching, sleepGoalCollections, sleepCollectionProducts } =
    useSleepProductCollection();

  if (isFetching || isEmpty(sleepGoalCollections)) {
    return <LoadingSpinner />;
  }

  const subCollectionData = sleepGoalCollections[staticData.collectionKey];
  const { title, image } = subCollectionData.metadata;
  const {
    secondaryTitle,
    introText,
    featuredProductsIntroText,
    featuredProduct,
    editorial,
    imgixProps,
    styles = {},
  } = staticData;
  const featuredProductsIntro = featuredProductsIntroText || secondaryTitle;
  const featuredProductData = { ...featuredProduct, editorial };
  const collectionTitle = `${title} Collection`;

  return (
    <main className="dream_shop_collection_page_base page">
      <Seo title={collectionTitle} />

      <SleepHeroSection
        header={
          <header className="header" {...getBannerBackground(styles)}>
            <h1 className="heading h1_text">{title}</h1>
            <h2 className="subheading caption_primary">{secondaryTitle}</h2>
          </header>
        }
        image={image}
        imgixProps={imgixProps}
        altText={collectionTitle}
      />

      <IntroSection copy={introText} />

      <FeaturedSleepProductSection
        staticData={featuredProductData}
        sleepProducts={sleepCollectionProducts}
      />

      <FeaturedProductsIntroSection copy={featuredProductsIntro} />

      <FeaturedProductsSection
        className="featured_products full_width_section"
        renderHeader={FeaturedProductsHeader}
        productList={subCollectionData.products}
        productLimit={120}
        subCollectionData={subCollectionData}
        staticData={staticData}
      />
    </main>
  );
};

DreamShopCollectionPageBase.propTypes = {
  staticData: PropTypes.shape({
    secondaryTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
      .isRequired,
    introText: PropTypes.object.isRequired,
    featuredProduct: PropTypes.shape({
      handle: PropTypes.string.isRequired,
      features: PropTypes.array.isRequired,
      intro: PropTypes.string.isRequired,
      image: PropTypes.object.isRequired,
    }).isRequired,
    editorial: PropTypes.string.isRequired,
    featuredProductsIntroText: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ]),
    imgixProps: PropTypes.shape({
      mobile: PropTypes.object,
      tablet: PropTypes.object,
      desktop: PropTypes.object,
    }),
    styles: PropTypes.shape({
      sectionBackground: PropTypes.string,
      bannerBackground: PropTypes.string,
    }),
  }),
};

export default DreamShopCollectionPageBase;
